import { action, observable } from 'mobx';
import stores from '../../../models/Stores';

const efh2Store = stores.AppStore.efh2Store;
export class RainfallDischargeDataVM {
  @observable props;
  constructor(props: any) {
    this.props = props;
  }
  @observable selectAllHydrographs = false;
  @observable rainfallPlotData = {};
  @observable plotData = {};
  @observable targetDivID = 'hydrograph-div';
  @observable windowOptions = {
    margins: { top: 10, right: 90, bottom: 40, left: 60 },
    height: 450,
    ariaLabelForLines: [' peak discharge is ', ' cubic feet per second at ', ' hours'],
    ariaLabel:
      'Hydrograph for various storms with varying severity showing discharge on the y axis and time on the x axis.',
  };
  @observable rainfallDischargeTableReset: any = [
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
    { frequency: 0, rainfall: 0, peakFlow: 0, runoff: 0, selected: false },
  ];
  @observable userPreferences = stores.AppStore.userProfileStore.userPreferences;

  @action.bound toggleSelectAll(value) {
    this.selectAllHydrographs = value;
    for (let i = 0; i < efh2Store.rainfallDischargeTable.length; i++) {
      efh2Store.rainfallDischargeTable[i].selected = value;
    }
    this.gatherDataAndPlot();
  }

  @action.bound toggleByUserPref() {
    this.userPreferences = stores.AppStore.userProfileStore.userPreferences;
    const userPreferences = stores.AppStore.userProfileStore.getPreference('analysis-hydrology-project');
    if (userPreferences && userPreferences.storm_hydrograph) {
      this.selectAllHydrographs = false;
      for (let i = 0; i < efh2Store.rainfallDischargeTable.length; i++) {
        efh2Store.rainfallDischargeTable[i].selected = userPreferences.storm_hydrograph[i];
      }
      this.gatherDataAndPlot();
    } else {
      this.toggleSelectAll(true);
    }
  }

  @action.bound selectHydrograph(index, value) {
    const row = efh2Store.rainfallDischargeTable[index];
    row.selected = value;
    this.gatherDataAndPlot();
  }

  private gatherDataAndPlot() {
    const keys = Object.keys(this.rainfallPlotData);
    const selectedPlots: any = {};
    for (const idx in keys) {
      const tableRow = efh2Store.rainfallDischargeTable[idx];
      if (tableRow.selected) {
        const key = keys[idx];
        selectedPlots[key] = this.rainfallPlotData[key];
      }
    }
    this.plotData = selectedPlots;
  }

  @action.bound handleInputChange(event: { target: any }) {
    const target = event.target;
    const value = target.value;
    const name = target.name;
    const index = +target.id.replace(name, '');
    const id = target.id;
    efh2Store.rainfallDischargeTable[index][name] = value;
    this.validateField(value, id, index);
    // Re-validates following fields to not keep resolved errors
    for (let i = index; i < 9; i++) {
      this.validateField(efh2Store.rainfallDischargeTable[i][name], name + i, i);
    }
  }

  validateRainfallDischargeTable = () => {
    // only need to check the rainfall col
    for (let i = 0; i < efh2Store.rainfallDischargeTable.length; i++) {
      this.validateField(efh2Store.rainfallDischargeTable[i]['rainfall'], 'rainfall' + i, i);
    }
  };

  @action.bound handleOnBlurFrequency(event: { target: any }) {
    const target = event.target;
    const name = target.name;
    const index = +target.id.replace(name, '');
    const oldValue = target.value;
    const newValue = Math.trunc(parseFloat(oldValue));
    efh2Store.rainfallDischargeTable[index][name] = newValue;
  }

  @action.bound handleOnBlurRainfall(event: { target: any }) {
    const target = event.target;
    const name = target.name;
    const index = +target.id.replace(name, '');
    const oldValue = target.value;
    const newValue = parseFloat(oldValue).toFixed(2);
    efh2Store.rainfallDischargeTable[index][name] = newValue;
  }

  @action.bound handleCheckboxChange(event: { target: any }) {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    const index = +target.id.replace(name, '');
    efh2Store.rainfallDischargeTable[index][name] = value;
    // set user preference in data store
    const stormSelection = efh2Store.rainfallDischargeTable.map((element) => {
      return element.selected;
    });
    if (stores.AppStore.userProfileStore.userPreferences) {
      stores.AppStore.userProfileStore.setPreference('analysis-hydrology-project', 'storm_hydrograph', stormSelection);
    }
  }

  @observable formValidation = {
    formErrors: {
      frequency: new Array(9).fill(''),
      rainfall: new Array(9).fill(''),
      peakFlow: new Array(9).fill(''),
      runoff: new Array(9).fill(''),
    },
    frequencyColumn: new Array(9).fill(false),
    rainfallColumn: new Array(9).fill(false),
    peakFlowColumn: new Array(9).fill(false),
    runoffColumn: new Array(9).fill(false),
    formValid: false,
  };

  // This is where the actual validation checks occur
  private errorMessageChecks(fieldValidationErrors, value, fieldValid, index, name) {
    if (value === '') {
      fieldValid = false;
      fieldValidationErrors[name][index] = 'Input must not be empty';
    } else if (value < 0) {
      fieldValid = false;
      fieldValidationErrors[name][index] = 'No negative numbers allowed';
    } else if (value > 30) {
      fieldValid = false;
      fieldValidationErrors[name][index] = '24 hr rainfall cannot exceed 30in';
    } else {
      fieldValid = true;
      fieldValidationErrors[name][index] = '';

      // If the values pass validation, send them to the global store so they can be sent in the post request
      // switch (index) {
      //   case 0:
      //     stores.AppStore.efh2Store.basicDataForm.prec1 = value;
      //     break;
      //   case 1:
      //     stores.AppStore.efh2Store.basicDataForm.prec2 = value;
      //     break;
      //   case 2:
      //     stores.AppStore.efh2Store.basicDataForm.prec5 = value;
      //     break;
      //   case 3:
      //     stores.AppStore.efh2Store.basicDataForm.prec10 = value;
      //     break;
      //   case 4:
      //     stores.AppStore.efh2Store.basicDataForm.prec25 = value;
      //     break;
      //   case 5:
      //     stores.AppStore.efh2Store.basicDataForm.prec50 = value;
      //     break;
      //   case 6:
      //     stores.AppStore.efh2Store.basicDataForm.prec100 = value;
      //     break;
      //   default:
      //     break;
      // }
    }
  }

  private validateField(value, id, index) {
    // connects form field to validation table
    const name = id.slice(0, -1);
    const column = name + 'Column';
    const fieldValid = this.formValidation[column][index];
    const fieldValidationErrors = this.formValidation.formErrors;

    for (let i = 0; i < efh2Store.rainfallDischargeTable.length; i++) {
      switch (id) {
        case 'frequency' + i:
          this.errorMessageChecks(fieldValidationErrors, value, fieldValid, index, name);
          break;
        case 'rainfall' + i:
          this.errorMessageChecks(fieldValidationErrors, value, fieldValid, index, name);
          break;
        case 'peakFlow' + i:
          this.errorMessageChecks(fieldValidationErrors, value, fieldValid, index, name);
          break;
        case 'runoff' + i:
          this.errorMessageChecks(fieldValidationErrors, value, fieldValid, index, name);
          break;
        default:
          break;
      }
    }

    this.sortRainfallAscending();

    this.formValidation.formErrors = fieldValidationErrors;
    this.formValidation.rainfallColumn[index] = fieldValid;
    this.validateForm();
  }

  // Ensures rainfall column numbers will sort ascendingly
  private sortRainfallAscending() {
    let value = efh2Store.rainfallDischargeTable[0].rainfall;
    for (let i = 1; i < efh2Store.rainfallDischargeTable.length; i++) {
      // if the input has something in it (not empty and not NaN)
      if (efh2Store.rainfallDischargeTable[i].rainfall) {
        // Current value is bigger than old value and no error logged for this value
        if (
          +value <= +efh2Store.rainfallDischargeTable[i].rainfall &&
          this.formValidation.formErrors['rainfall'][i] === ''
        ) {
          this.formValidation.rainfallColumn[i] = true;
          this.formValidation.formErrors['rainfall'][i] = '';
        } // Current value is less than old value and is not negative
        else if (
          +efh2Store.rainfallDischargeTable[i].rainfall >= 0 &&
          +efh2Store.rainfallDischargeTable[i].rainfall <= 30
        ) {
          this.formValidation.formErrors['rainfall'][i] = 'Value cannot be less than preceeding value';
          this.formValidation.rainfallColumn[i] = false;
        }
      }
      value = efh2Store.rainfallDischargeTable[i].rainfall;
    }
  }

  private validateForm() {
    let valid = true;
    for (const key in this.formValidation.formErrors) {
      if (!this.formValidation[key + 'Valid']) {
        valid = false;
      }
    }
    this.formValidation.formValid = valid;
  }

  @action.bound errorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage.length === 0 ? '' : ' usa-input--error';
  }
}
